import React from "react";
import { IonIcon, IonModal } from "@ionic/react";
import {
  rocketOutline,
  arrowUpCircleOutline,
  closeOutline,
} from "ionicons/icons";

interface AppUpdateModalProps {
  isOpen: boolean;
  forceUpdate: boolean;
  message: string;
  storeUrl: string;
  onDismiss?: () => void;
  onUpdate: (storeUrl: string) => void;
  isChecking?: boolean;
}

export const AppUpdateModal: React.FC<AppUpdateModalProps> = ({
  isOpen,
  forceUpdate,
  message,
  storeUrl,
  onDismiss,
  onUpdate,
  isChecking = false,
}) => {
  return (
    <IonModal
      isOpen={isOpen}
      mode="ios"
      backdropDismiss={!forceUpdate}
      onDidDismiss={() => {
        if (!forceUpdate && onDismiss) onDismiss();
      }}
      className="app-update-modal !bg-transparent"
      style={{
        "--background": "transparent",
        "--ion-background-color": "transparent",
        "--backdrop-opacity": "0",
        "--box-shadow": "none",
        "--width": "100%",
        "--height": "100%",
        "--border-radius": "0",
      }}
    >
      <div className={`flex flex-col items-center justify-center h-full w-full px-4 ${forceUpdate ? "bg-black/40 backdrop-blur-sm" : "bg-black/20 backdrop-blur-[2px]"}`}>
        {/* Card Container */}
        <div className="bg-white rounded-[24px] shadow-2xl p-8 w-full max-w-[340px] relative overflow-hidden flex flex-col items-center text-center animate-fade-in-up">
          {/* Brand Gradient Background (Top Bar) */}
          <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-[var(--app-gradient-start)] to-[var(--app-gradient-end)]"></div>

          {/* Close Button (Only if Optional) */}
          {!forceUpdate && (
            <button
              onClick={() => onDismiss?.()}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors p-1"
              aria-label="Close update dialog"
            >
              <IonIcon icon={closeOutline} className="text-2xl" />
            </button>
          )}

          {/* Illustration / Icon */}
          <div className="mt-4 mb-6 w-20 h-20 rounded-full bg-[#E6F7FB] flex items-center justify-center">
            <IonIcon
              icon={arrowUpCircleOutline}
              className="text-5xl text-[var(--ion-color-primary)]"
            />
          </div>

          {/* Title */}
          <h2 className="text-[22px] font-extrabold text-[#000000] mb-3 leading-tight font-[Figtree,sans-serif]">
            {forceUpdate ? "Update Required" : "New Version Available"}
          </h2>

          {/* Message Body */}
          <p className="text-[#5b6770] text-[15px] leading-relaxed mb-8 px-1">
            {message ||
              "We've made some improvements! Please update the app to continue enjoying the best experience."}
          </p>

          {/* Primary Button */}
          <button
            onClick={() => onUpdate(storeUrl)}
            disabled={isChecking}
            className={`w-full py-3.5 rounded-[35px] flex items-center justify-center space-x-2 font-bold text-white shadow-lg shadow-blue-500/20 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--ion-color-primary)] ${isChecking ? "opacity-75 cursor-wait" : "active:scale-95"
              }`}
            style={{
              background: "var(--ion-color-primary)",
              fontFamily: "Figtree, sans-serif",
            }}
          >
            {isChecking ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Verifying...</span>
              </>
            ) : (
              <>
                <IonIcon icon={rocketOutline} className="text-xl" />
                <span>Update App</span>
              </>
            )}
          </button>

          {/* Secondary Link (For Optional) */}
          {!forceUpdate && (
            <button
              onClick={() => onDismiss?.()}
              className="mt-4 text-sm font-semibold text-[#92949c] hover:text-[#5b6770] transition-colors"
            >
              Not now
            </button>
          )}
          {/* Footer Note (For Forced) */}
          {forceUpdate && (
            <p className="mt-4 text-[11px] text-[#92949c]">
              Current version is no longer supported.
            </p>
          )}
        </div>
      </div>
    </IonModal>
  );
};
